import deepFreeze from 'deep-freeze';
import type { Action, Input, Layout } from './types.js';
import { Console } from './types.js';
import { Command } from '#~/command/index.js';

/** Continuous actions. */
export const Continuous: ReadonlySet<Action> = new Set([
  Command.PTZ_TILT,
  Command.PTZ_PAN,
  Command.PTZ_ZOOM,
  Command.FOCUS_NEAR,
  Command.FOCUS_FAR
]);

/** @internal */
export const DeadZone = 0.1;

/** Default layout. */
export const DefaultLayout: Layout = Object.freeze({
  // Studio control.
  [Command.VIDEO_ARRANGEMENT]: { button: 8 },
  [Command.VIDEO_SOURCE_PREV]: [{ button: 12 }, { button: 14 }],
  [Command.VIDEO_SOURCE_NEXT]: [{ button: 13 }, { button: 15 }],
  [Command.CONTROL_PRESET_ADD]: { button: 1 },
  // Camera control.
  [Command.PTZ_PAN]: { axis: 0 },
  [Command.PTZ_TILT]: { axis: 1 },
  [Command.PTZ_ZOOM]: { axis: 3 },
  [Command.PTZ_ZOOM_OUT]: { button: 6 },
  [Command.PTZ_ZOOM_IN]: { button: 7 },
  [Command.PT_SPEED_DOWN]: { button: 4 },
  [Command.PT_SPEED_UP]: { button: 5 },
  [Command.FOCUS_MODE]: { button: 2 },
  [Command.FOCUS_FAR]: { button: 3 },
  [Command.FOCUS_NEAR]: { button: 0 },
  [Command.TEMPO_PT_TOGGLE]: { button: 10 },
  [Command.QUICK_STOP]: { button: 11 }
} satisfies Record<
  Exclude<Action, Command.FOCUS_STOP>,
  Exclude<Input | ReadonlyArray<Input>, false>
>);

/** Language constants. */
export const Lang = Object.freeze({
  console: {
    [Console.Generic]: 'Generic Controller',
    [Console.PlayStation]: 'Sony PlayStation',
    [Console.Switch]: 'Nintendo Switch',
    [Console.Xbox]: 'Microsoft Xbox'
  } satisfies Record<Console, string>
});

/** Console-specified layouts. For now, there is only one layout. */
export const Layouts = Object.freeze({
  [Console.Generic]: DefaultLayout,
  [Console.PlayStation]: DefaultLayout,
  [Console.Switch]: DefaultLayout,
  [Console.Xbox]: DefaultLayout
} satisfies Record<Console, Layout>);

/** Gamepad button manifest. */
export const Manifest = Object.freeze({
  [Command.VIDEO_ARRANGEMENT]: { icon: 'gamepad-b8', rank: 92, title: 'Back/Select/Share' },
  [Command.VIDEO_SOURCE_NEXT]: { icon: 'dpad-right', rank: 24, title: 'D-Pad Right' },
  [Command.VIDEO_SOURCE_PREV]: { icon: 'dpad-left', rank: 23, title: 'D-Pad Left' },
  [Command.CONTROL_PRESET_ADD]: { icon: 'gamepad-b1', rank: 44, title: 'B/Circle Button' },

  [Command.PTZ_PAN]: { icon: 'l-stick-x', rank: 10, title: 'Left Stick X-Axis' },
  [Command.PTZ_TILT]: { icon: 'l-stick-y', rank: 11, title: 'Left Stick Y-Axis' },
  [Command.PTZ_ZOOM]: { icon: 'r-stick-y', rank: 12, title: 'Right Stick Y-Axis' },
  [Command.PTZ_ZOOM_IN]: { icon: 'gamepad-b7', rank: 33, title: 'Right Trigger' },
  [Command.PTZ_ZOOM_OUT]: { icon: 'gamepad-b6', rank: 13, title: 'Left Trigger' },
  [Command.PT_SPEED_DOWN]: { icon: 'gamepad-b4', rank: 30, title: 'Left Bumper' },
  [Command.PT_SPEED_UP]: { icon: 'gamepad-b5', rank: 31, title: 'Right Bumper' },
  [Command.FOCUS_MODE]: { icon: 'gamepad-b2', rank: 42, title: 'X/Square Button' },
  [Command.FOCUS_FAR]: { icon: 'gamepad-b3', rank: 40, title: 'Y/Triangle Button' },
  [Command.FOCUS_NEAR]: { icon: 'gamepad-b0', rank: 41, title: 'A/X Button' },
  [Command.TEMPO_PT_TOGGLE]: { icon: 'l-stick-z', rank: 90, title: 'Left Stick Press/Click' },
  [Command.QUICK_STOP]: { icon: 'r-stick-z', rank: 91, title: 'Right Stick Press/Click' }
} satisfies Record<
  Exclude<Action, Command.FOCUS_STOP>,
  { icon: string[] | string; rank: number; title: string }
>);

/** @internal */
export const PrefKeys = Object.freeze({
  hint: 'gamepad.hint',
  lock: 'gamepad.zoom.lock',
  profiles: 'gamepad.profiles'
});

/** @internal */
export const Recall = deepFreeze({
  focus: [Command.FOCUS_NEAR, Command.FOCUS_FAR],
  movements: [Command.PTZ_PAN, Command.PTZ_TILT, Command.PTZ_ZOOM]
} satisfies Record<string, readonly Action[]>);
